<template>
  <div class="l-sport-tooltips">
    <div class="c-sport-tooltips__header">
      <div>
        <h3 class="c-sport-tooltips__title">
          {{ sport.rowData.Sport }}
        </h3>
        <p class="c-sport-tooltips__subtitle">
          Tooltips shown for this sport across websites.
        </p>
      </div>
      <button
        v-if="canWrite(`${PERMISSION_NAMES.CONFIGURATION.key}.${PERMISSION_NAMES.TOOLTIPS.key}`)"
        type="button"
        class="ui-btn ui-btn--primary"
        @click="createTooltips()"
      >
        <app-icon name="add" />
        Add tooltip
      </button>
    </div>

    <div class="ui-field c-sport-tooltips__search">
      <div class="ui-field__control">
        <app-icon
          class="ui-field__icon"
          name="search"
        />
        <input
          v-model="searchQuery"
          class="ui-field__input"
          type="text"
          placeholder="Search tooltips…"
          aria-label="Search tooltips"
          @input="handleSearch"
        >
      </div>
    </div>

    <div class="c-table-wrapper l-tooltips__table c-sport-tooltips__table">
      <base-table-component
        :prop-data="tooltipsForSportTableData"
        :empty-title="searchQuery ? 'No tooltips found' : 'No tooltips for this sport yet'"
        :empty-description="searchQuery ? `Nothing matches “${searchQuery}”.` : 'Add a tooltip to get started.'"
      >
        <template v-slot:default="slotProps">
          <span class="c-table__btn-wrap">
            <generic-button
              icon
              transparent
              text-inherit
              :disabled="!canWrite(`${PERMISSION_NAMES.CONFIGURATION.key}.${PERMISSION_NAMES.TOOLTIPS.key}`)"
              @click="editTooltips(slotProps.selected)"
            >
              <md-tooltip md-direction="top">
                Edit sport tooltips
              </md-tooltip>
              <img src="../../../../../../../assets/edit-button.svg" alt="">
            </generic-button>
          </span>
          <span class="c-table__btn-wrap">
            <generic-button
              icon
              transparent
              text-inherit
              :disabled="!canWrite(`${PERMISSION_NAMES.CONFIGURATION.key}.${PERMISSION_NAMES.TOOLTIPS.key}`)"
              @click="openDeleteModal(slotProps.selected)"
            >
              <md-tooltip md-direction="top">
                Delete sport tooltips
              </md-tooltip>
              <img src="../../../../../../../assets/delete-button.svg" alt="">
            </generic-button>
          </span>
        </template>
      </base-table-component>
    </div>

    <paginate
      v-if="totalPages > 1"
      v-model="currentPage"
      :page-count="totalPages"
      :prev-text="'<'"
      :next-text="'>'"
      :page-range="20"
      :container-class="'c-pagination'"
      :prev-link-class="'md-button prev md-elevation-1'"
      :next-link-class="'md-button next md-elevation-1'"
      :page-link-class="'md-button md-elevation-1'"
      :active-class="'active'"
      :disabled-class="'disabled'"
    />
  </div>
</template>

<script>
import GenericButton from '@/shared/buttons/GenericButton';
import BaseTableComponent from '@/shared/table/BaseTableComponent';
import {tooltipsService} from '@/services/tooltips';
import EditTooltipsModal from '../edit';
import {responseService} from '@/services/response';
import {mapActions} from 'vuex';
import DeleteModalComponent from '@/shared/delete-modal/DeleteModalComponent.vue';
import { wordService } from '@/services/words';
import each from 'lodash/each';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import {onlinePermissionsMixin} from "@/mixins/onlinePermissionsMixin";
import {PERMISSION_NAMES} from "@/const/online-permissions";
import AppIcon from '@/shared/app-icon/AppIcon';

export default {
    name: 'SportTooltips',
    itemsPerPage: 10,

    components: {
      GenericButton,
      BaseTableComponent,
      AppIcon,
    },

  mixins: [onlinePermissionsMixin],

    props: {
      website: {
        type: Object,
        default: null,
      },
      sport: {
        type: Object,
        default: null
      },
      goBack: {
        type: Function,
        default: () => {}
      },
      languages: {
        type: Array,
        default: () => []
      },
      tooltipSection: {
        type: Object,
        default: null
      }
    },

    data() {
      return {
        searchQuery: '',

        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,

        fetchedResults: false,
        tooltipsForSport: [],
        updateData: {},
        listOfPhraseKeys: [],
        listOfTemplateIds: [],
        translations: {},
      }
    },

    computed: {
      PERMISSION_NAMES() {
        return PERMISSION_NAMES
      },
      tooltipsForSportTableData() {
        if (!this.tooltipsForSport) {
          return [];
        }

        const currentPage = this.currentPage || 1;
        const unfilteredTooltips = this.tooltipsForSport.map((tooltipForSport, index) => ({
          tooltipIndex: index,
          tooltipForSport: tooltipForSport,
          rowData: {
            'Phrase key': tooltipForSport.phrase_key,
            'Template Ids': tooltipForSport.template_ids.join(', '),
          },
        }))

        return this.filterTooltipsForSport(unfilteredTooltips)
          .slice((currentPage - 1) * this.itemsPerPage, currentPage * this.itemsPerPage);
      },
    },

    async created() {
      this.getTooltipsForSport(true);
    },

    methods: {
      ...mapActions("response", ["handleSuccess", "setGlobalError", "resetError"]),

      async handleCreatedTooltip() {
        await this.getTooltipsForSport();
        this.currentPage = this.totalPages;
      },

      async getTooltipsForSport(isInit = false) {
        try {
          const tooltipsForSportResponse = await tooltipsService.getSingleSportTooltipsBySportId(this.sport.sportWithTooltip.sportId)
          this.updateData = tooltipsForSportResponse.data;
          this.tooltipsForSport = JSON.parse(tooltipsForSportResponse.data.tooltips);
          this.fetchedResults = true;
          this.listOfPhraseKeys = [];
          this.listOfTemplateIds = [];

          each(this.tooltipsForSport, (tooltip) => {
            this.listOfPhraseKeys.push(tooltip.phrase_key);
            this.listOfTemplateIds.push(...tooltip.template_ids);
          })

          this.handlePaginationParameters()

          if (!isInit || this.tooltipsForSport.length) {
            return;
          }

          this.createTooltips();
        } catch (error) {
          this.goBack();
          this.setGlobalError(responseService.getErrorMessage(error.data.message));
        }
      },

      async editTooltips(tooltip) {
        this.$modal.show(
          EditTooltipsModal,
          {
            fetchedTooltip: {
              phrase_key: tooltip.tooltipForSport.phrase_key,
              template_ids: [...tooltip.tooltipForSport.template_ids],
              tooltips: [...tooltip.tooltipForSport.tooltips],
            },
            handleSuccessCallback: this.getTooltipsForSport,
            allTooltips: this.tooltipsForSport,
            tooltipIndex: tooltip.tooltipIndex,
            sportId: this.sport.sportWithTooltip.sportId,
            websiteIds: this.sport.sportWithTooltip.websiteIds,
            listOfPhraseKeys: this.listOfPhraseKeys,
            listOfTemplateIds: this.listOfTemplateIds,
            languages: this.languages,
            tooltipSection: this.tooltipSection
          },
          { height: 'auto', scrollable: true, width: '50%' }
        );
      },

      createTooltips() {
        this.$modal.show(
          EditTooltipsModal,
          {
            handleSuccessCallback: this.handleCreatedTooltip,
            allTooltips: this.tooltipsForSport,
            sportId: this.sport.sportWithTooltip.sportId,
            websiteIds: this.sport.sportWithTooltip.websiteIds,
            listOfPhraseKeys: this.listOfPhraseKeys,
            listOfTemplateIds: this.listOfTemplateIds,
            languages: this.languages,
            tooltipSection: this.tooltipSection
          },
          { height: 'auto', scrollable: true, width: '50%' }
        );
      },

      async deleteTooltipForSport(tooltip) {
        try {
          await tooltipsService.deleteTooltips({
            sportId: this.sport.sportWithTooltip.sportId,
            phraseKey: tooltip.tooltipForSport.phrase_key
          })
          await this.handleDeleteTranslationsForTooltip(tooltip);
          this.handleSuccess({ message: 'Successfully deleted Tooltip' })
        } catch (error) {
          this.setGlobalError(responseService.getErrorMessage(error.data?.message));
        }
      },

      openDeleteModal(tooltip) {
        this.$modal.show(
          DeleteModalComponent,
          {
            deleteMsg: tooltip.tooltipForSport.phrase_key,
            deleteMsgAlert: 'It will be deleted on all websites.',
            propData: tooltip,
            deleteFunction: this.deleteTooltipForSport,
            getDataFunction: this.getTooltipsForSport,
            pageData: {}
          },
          { height: "auto", scrollable: false, width: "440px" }
        );
      },

      handlePaginationParameters(totalItemsCount = null) {
        const tooltipsCount = totalItemsCount !== null ? totalItemsCount : this.tooltipsForSport.length;

        this.totalPages = Math.ceil(tooltipsCount / this.itemsPerPage)
        this.totalItems = tooltipsCount;

        if (this.currentPage > this.totalPages) {
          this.currentPage = this.totalPages;
        }
      },

      handleSearch() {
        const filteredTooltips = this.filterTooltipsForSport(this.tooltipsForSport);
        this.handlePaginationParameters(filteredTooltips.length)
      },

      async fetchTranslations(term, currentPage = 1, limit = 200) {
        return await wordService.getOnlineAll(currentPage, limit, 'tooltips', term).then(response => {
          const translations = response.data.items.filter((single) => {
            let r = new RegExp(`^${term}$`);
            return single.key.match(r) || (includes(single.key, `_${term}_`))
          });
          return translations;
        })
      },

      deleteWord(id) {
        return wordService.deleteOnline(id);
      },

      getTranslationPhraseKeyFromSelectionAndTooltip(selection, tooltip) {
        return `${this.sport.sportWithTooltip.sportId}_${tooltip.tooltipForSport.phrase_key}_${selection}`
      },

      deleteTranslationByPhraseKey(selection) {
        const word = this.translations[this.getTranslationPhraseKeyFromSelection(selection)] ||
          find(this.listOfTemporaryWords, tempWord => tempWord.word === this.getTranslationPhraseKeyFromSelection(selection));
        const id = word?.wordId || word?.id;
        if (id) {
          this.deleteWord(id);
        }
      },

      async handleDeleteTranslationsForTooltip(tooltip) {
        const translations = await this.fetchTranslations(tooltip.tooltipForSport.phrase_key);

        if (!translations.length) {
          return;
        }

        each(translations, translation => {
          wordService.deleteOnline(translation.id);
        })
      },

      filterTooltipsForSport(unfilteredTooltips) {
        return filter(unfilteredTooltips, tooltipForSport => {
          if (tooltipForSport.rowData) {
            return tooltipForSport.tooltipForSport.phrase_key.toLowerCase().includes(this.searchQuery.toLowerCase())
                || tooltipForSport.tooltipForSport.template_ids.join(',').includes(this.searchQuery)
          }

          return tooltipForSport.phrase_key.toLowerCase().includes(this.searchQuery.toLowerCase())
            || tooltipForSport.template_ids.join(',').includes(this.searchQuery)
        })
      }
    }
  }
</script>

<style lang="scss" scoped>
.c-sport-tooltips__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.c-sport-tooltips__title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.c-sport-tooltips__subtitle {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
}

.c-sport-tooltips__search {
  max-width: 360px;
  margin-bottom: 16px;

  .ui-field__input {
    height: 40px;
    font-size: 14px;
  }
}

.c-sport-tooltips__table {
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  overflow: hidden;
}
.c-sport-with-tooltip-header {
  border-bottom: 2px solid gray;
  margin-bottom: 5px;
}
</style>
